import * as React from 'react';
import {
  Filter,
  FilterChangeEvent,
  Operators,
} from '@progress/kendo-react-data-tools';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import '@progress/kendo-theme-bootstrap/dist/all.css';
import { createStyles, makeStyles } from '@material-ui/core';
import { Dispatch } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { ASSET } from './../../utils/constants';
import { GetString } from '../StringResourceManager';
import { useEffect } from 'react';
import { LocalizationProvider, loadMessages } from '@progress/kendo-react-intl';
import { locales, LocaleInterface } from '../AlertsGrid/GridLocale';
import esMessages from '../../utils/gridMessageKey.json';
import { saveHealthFilters } from '../../store/UserSettings/UserSettingsActions';
import { NodeVersionPropertyValues } from '../../Interfaces/Versions/NodeVersionPropertyValues';
loadMessages(esMessages, 'fr-CA');
import { ApiEnums } from '../../Interfaces/ApiEnums';
import { isMatch } from '../../utils/textMappings';
import DynamicComboBox from '../DynamicComboBox';

const useStyles = makeStyles(() =>
  createStyles({
    filterArea: {
      Height: '100%',
      width: '100%',
      overflow: 'auto',
    },
  })
);

interface Props {
  activeThreshold: number;
}

const HealthFilter: React.FC<Props> = ({
  activeThreshold,
}): React.ReactElement => {
  const classes = useStyles();
  const dispatch: Dispatch<any> = useDispatch();

  const filter: CompositeFilterDescriptor = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.healthFilter
  );

  const onFilterChange = (event: FilterChangeEvent) => {
    saveHealthFilters(dispatch, event.filter);
  };

  const siteNumbers: string[] = useSelector(
    (state: ApplicationState) => state.assetsReducer.siteNumbers
  );

  const divisions: string[] = useSelector(
    (state: ApplicationState) => state.assetsReducer.divisions
  );

  const clusters: string[] = useSelector(
    (state: ApplicationState) => state.assetsReducer.clusters
  );

  const laneNumbers: string[] = useSelector(
    (state: ApplicationState) => state.assetsReducer.laneNumbers
  );

  const nodeTypes: string[] = useSelector(
    (state: ApplicationState) => state.assetsReducer.nodeTypes
  );

  const nodeVersionPropertyValues: NodeVersionPropertyValues = useSelector(
    (state: ApplicationState) => state.versionsReducer.nodeVersionPropertyValues
  );

  const [currentLocale, setCurrentLocale] = React.useState<LocaleInterface>(
    locales[0]
  );

  const selectedLanguage: string = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.selectedLanguage
  );

  useEffect(() => {
    if (selectedLanguage == 'English') {
      setCurrentLocale(locales[0]);
    } else if (selectedLanguage == 'French') {
      setCurrentLocale(locales[1]);
    }
  }, [selectedLanguage]);

  const CustomSiteNumberFilter = (props: any) => {
    const onChangeSiteNumber = (event: any) => {
      props.onFilterChange.call(undefined, {
        nextFilter: { ...props.filter, value: event.target.value },
      });
    };
    return (
      <DynamicComboBox
        onChange={onChangeSiteNumber}
        data={siteNumbers}
        value={props.filter.value}
        placeholder={GetString('STORE_PLACEHOLDER')}
      />
    );
  };

  const CustomClusterFilter = (props: any) => {
    const onChangeCluster = (event: any) => {
      props.onFilterChange.call(undefined, {
        nextFilter: { ...props.filter, value: event.target.value },
      });
    };
    return (
      <DynamicComboBox
        onChange={onChangeCluster}
        data={clusters}
        value={props.filter.value}
        placeholder={GetString('CLUSTER_PLACEHOLDER')}
      />
    );
  };

  const CustomLaneNumberFilter = (props: any) => {
    const onChangeLaneNumber = (event: any) => {
      props.onFilterChange.call(undefined, {
        nextFilter: { ...props.filter, value: event.target.value },
      });
    };
    return (
      <DynamicComboBox
        onChange={onChangeLaneNumber}
        data={laneNumbers}
        value={props.filter.value}
        placeholder={GetString('LANE_PLACEHOLDER')}
      />
    );
  };

  const CustomNodeTypeFilter = (props: any) => {
    const onChangeNodeType = (event: any) => {
      props.onFilterChange.call(undefined, {
        nextFilter: { ...props.filter, value: event.target.value },
      });
    };
    return (
      <DynamicComboBox
        onChange={onChangeNodeType}
        data={nodeTypes}
        value={props.filter.value}
        placeholder={GetString('NODE_TYPE_PLACEHOLDER')}
      />
    );
  };

  const CustomDivisionFilter = (props: any) => {
    const onChangeDivision = (event: any) => {
      props.onFilterChange.call(undefined, {
        nextFilter: { ...props.filter, value: event.target.value },
      });
    };
    return (
      <DynamicComboBox
        onChange={onChangeDivision}
        data={divisions}
        value={props.filter.value}
        placeholder={GetString('DIVISION_PLACEHOLDER')}
      />
    );
  };

  const CustomDeliveryCodeFilter = (props: any) => {
    const onChangeDeliveryCode = (event: any) => {
      props.onFilterChange.call(undefined, {
        nextFilter: { ...props.filter, value: event.target.value },
      });
    };
    return (
      <DynamicComboBox
        onChange={onChangeDeliveryCode}
        data={nodeVersionPropertyValues.DeliveryCodes}
        value={props.filter.value}
        placeholder={GetString('DELIVERY_CODE')}
      />
    );
  };

  const CustomImageVersionFilter = (props: any) => {
    const onChangeImageVersion = (event: any) => {
      props.onFilterChange.call(undefined, {
        nextFilter: { ...props.filter, value: event.target.value },
      });
    };
    return (
      <DynamicComboBox
        onChange={onChangeImageVersion}
        data={nodeVersionPropertyValues.ImageVersions}
        value={props.filter.value}
        placeholder={GetString('IMAGE_VERSION')}
      />
    );
  };

  const CustomPackageNameFilter = (props: any) => {
    const onChangePackageName = (event: any) => {
      props.onFilterChange.call(undefined, {
        nextFilter: { ...props.filter, value: event.target.value },
      });
    };
    return (
      <DynamicComboBox
        onChange={onChangePackageName}
        data={nodeVersionPropertyValues.PackageNames}
        value={props.filter.value}
        placeholder={GetString('PACKAGE_NAME')}
      />
    );
  };

  const CustomBuildNumberFilter = (props: any) => {
    const onChangeBuildNumber = (event: any) => {
      props.onFilterChange.call(undefined, {
        nextFilter: { ...props.filter, value: event.target.value },
      });
    };
    return (
      <DynamicComboBox
        onChange={onChangeBuildNumber}
        data={nodeVersionPropertyValues.BuildNumbers}
        value={props.filter.value}
        placeholder={GetString('BUILD_NUMBER')}
      />
    );
  };

  const CustomLaneStatusFilter = (props: any) => {
    const onChangeLaneStatusFilter = (event: any) => {
      props.onFilterChange.call(undefined, {
        nextFilter: {
          ...props.filter,
          value: {
            Status: getLaneStatusKey(event.target.value),
            ActiveThreshold: activeThreshold,
          },
        },
      });
    };
    return (
      <DynamicComboBox
        onChange={onChangeLaneStatusFilter}
        data={[GetString('REPORTING'), GetString('NOT_REPORTING')]}
        value={getComboBoxValue(props.filter?.value?.Status) ?? ''}
        placeholder={GetString('LANE_STATUS')}
      />
    );
  };

  const getLaneStatusKey = (value: any) => {
    if (isMatch('REPORTING', value))
      return ApiEnums.NodeReportingStatus.Reporting;
    else if (isMatch('NOT_REPORTING', value))
      return ApiEnums.NodeReportingStatus.NotReporting;
    else return ApiEnums.NodeReportingStatus.Unknown;
  };

  const getComboBoxValue = (value: ApiEnums.NodeReportingStatus) => {
    if (value == ApiEnums.NodeReportingStatus.Reporting)
      return GetString('REPORTING');
    else if (value == ApiEnums.NodeReportingStatus.NotReporting)
      return GetString('NOT_REPORTING');
  };

  useEffect(() => {
    var newFilter: CompositeFilterDescriptor = filter;

    newFilter.filters?.map((data: any) => {
      if (
        data?.field !== undefined &&
        data?.field == ASSET.LANE_STATUS &&
        data?.value?.ActiveThreshold !== undefined
      ) {
        data.value.ActiveThreshold = activeThreshold;
      }
    });

    saveHealthFilters(dispatch, newFilter);
  }, [activeThreshold]);

  return (
    <React.Fragment>
      <LocalizationProvider language={currentLocale.language}>
        <Filter
          value={filter}
          className={classes.filterArea}
          onChange={onFilterChange}
          fields={[
            {
              name: ASSET.FIELD_DIVISION_NAME,
              label: GetString('FIELD_DIVISION_LABEL'),
              filter: CustomDivisionFilter,
              operators: Operators.text,
            },
            {
              name: ASSET.FIELD_STORE_NAME,
              label: GetString('FIELD_STORE_LABEL'),
              filter: CustomSiteNumberFilter,
              operators: Operators.text,
            },
            {
              name: ASSET.FIELD_CLUSTER_NAME,
              label: GetString('FIELD_CLUSTER_LABEL'),
              filter: CustomClusterFilter,
              operators: Operators.text,
            },
            {
              name: ASSET.FIELD_LANE_NAME,
              label: GetString('FIELD_LANE_LABEL'),
              filter: CustomLaneNumberFilter,
              operators: Operators.text,
            },
            {
              name: ASSET.FIELD_NODETYPE_NAME,
              label: GetString('FIELD_NODETYPE_LABEL'),
              filter: CustomNodeTypeFilter,
              operators: Operators.text,
            },
            {
              name: ASSET.DELIVERY_CODE,
              label: GetString('DELIVERY_CODE'),
              filter: CustomDeliveryCodeFilter,
              operators: Operators.text,
            },
            {
              name: ASSET.IMAGE_VERSION,
              label: GetString('IMAGE_VERSION'),
              filter: CustomImageVersionFilter,
              operators: Operators.text,
            },
            {
              name: ASSET.BUILD_NUMBER,
              label: GetString('BUILD_NUMBER'),
              filter: CustomBuildNumberFilter,
              operators: Operators.text,
            },
            {
              name: ASSET.PACKAGE_NAME,
              label: GetString('PACKAGE_NAME'),
              filter: CustomPackageNameFilter,
              operators: Operators.text,
            },
            {
              name: ASSET.LANE_STATUS,
              label: GetString('LANE_STATUS'),
              filter: CustomLaneStatusFilter,
              operators: Operators.text,
            },
          ]}
        />
      </LocalizationProvider>
    </React.Fragment>
  );
};

export default HealthFilter;
